import _isEqual from 'lodash/isEqual'
import RemoteFiltersApiService from 'frontcore/presentation/remote-filters/data/remote-filters-api-service'
import useRemoteFiltersSearch from 'frontcore/presentation/remote-filters/data/use-remote-filters-search'
import useRemoteFiltersUrl from 'frontcore/presentation/remote-filters/data/use-remote-filters-url'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  RemoteFiltersChip,
  RemoteFiltersConfiguration,
  RemoteFiltersFieldType,
  RemoteFiltersStore,
  RemoteFiltersStoreConfiguration,
} from 'frontcore/presentation/remote-filters/domain/types'
import { RemoteFiltersValue } from 'frontcore/presentation/remote-filters/domain/remote-filters-value'
import { useRoute, useRouter } from 'vue-router'

import type { RouteLocation, Router } from 'vue-router'

const getQueryParameters = (remoteFilterValues: RemoteFiltersValue[]): Record<string, string> => {
  let parameters = {}
  for (const value of Object.values(remoteFilterValues)) {
    const queryParameters = value.queryParameters
    if (queryParameters === undefined) { continue }
    parameters = { ...parameters, ...queryParameters }
  }
  return parameters
}

const useRemoteFiltersStore = (filterURL: string, storeConfiguration?: RemoteFiltersStoreConfiguration, overriddenRoute?: RouteLocation, overridenRouter?: Router) => {
  const remoteFilterStoreID = storeConfiguration == null ? filterURL : `${filterURL}${storeConfiguration.id}`
  const storeConfigParams = storeConfiguration?.extra ?? {}

  return defineStore(remoteFilterStoreID, (): RemoteFiltersStore => {
    const useSearch = useRemoteFiltersSearch()
    const route = overriddenRoute ?? useRoute()
    const router = overridenRouter ?? useRouter()

    const {
      getURLFilterParameter,
      setURLFilterParameters,
    } = useRemoteFiltersUrl(route, router)

    const remoteFiltersConfigurations = ref<RemoteFiltersConfiguration[]>([])
    const remoteFilters = ref<Record<string, RemoteFiltersValue>>({})
    const stagedFilters = ref<Record<string, RemoteFiltersValue>>({})

    const initialFetch = async () => {
      if (remoteFiltersConfigurations.value.length === 0) {
        await fetchRemoteFiltersConfiguration(route.query)
        setRemoteFiltersValuesFromURL()
      } else {
        commitChanges()
      }
    }

    const fetchRemoteFiltersConfiguration = async (routeQueryParams: object) => {
      const service = new RemoteFiltersApiService(filterURL)
      const configurations = await service.fetch({
        ...routeQueryParams,
        ...storeConfigParams,
      })
      remoteFiltersConfigurations.value.splice(0, Number.MAX_SAFE_INTEGER, ...configurations)
    }

    const setRemoteFiltersValuesFromURL = () => {
      for (const configuration of remoteFiltersConfigurations.value) {
        stagedFilters.value[configuration.filterId] = getURLFilterParameter(configuration) ?? RemoteFiltersValue.buildDefault(configuration)
      }
      commitChanges()
    }

    const isFilterActiveInModule = (configuration: RemoteFiltersConfiguration): boolean => {
      return configuration.modules?.some((module) => module === route.name) ?? true
    }

    const isFilterChanged = (configuration: RemoteFiltersConfiguration): boolean => {
      return !_isEqual(
        stagedFilters.value[configuration.filterId]?.value,
        RemoteFiltersValue.buildDefault(configuration).value
      ) && isFilterActiveInModule(configuration)
    }

    const moduleRemoteFiltersConfigurations = computed<RemoteFiltersConfiguration[]>(() => {
      return remoteFiltersConfigurations.value.filter(isFilterActiveInModule)
    })

    const moduleRemoteFilters = computed<Record<string, RemoteFiltersValue>>(() => {
      const filters: Record<string, RemoteFiltersValue> = {}
      for (const configuration of moduleRemoteFiltersConfigurations.value) {
        filters[configuration.filterId] = remoteFilters.value[configuration.filterId] as RemoteFiltersValue
      }
      return filters
    })

    const getRemoteFiltersValue = (filterId: string): RemoteFiltersValue | undefined => {
      return stagedFilters.value[filterId]
    }

    const setRemoteFiltersValue = (filterId: string, remoteFiltersValue: RemoteFiltersValue): void => {
      stagedFilters.value[filterId] = remoteFiltersValue

      if (
        remoteFiltersConfigurations.value.some(filterConfig => filterConfig.relatedFilters.includes(filterId))
        || remoteFiltersValue.configuration.reloadFiltersWhenUpdateValue
      ) {
        const queryParameters = getQueryParameters(Object.values(stagedFilters.value))
        fetchRemoteFiltersConfiguration(queryParameters).then(() => {
          commitChanges()
        })
      } else {
        commitChanges()
      }
    }

    const setDefaultRemoteFiltersInStagedFilters = () => {
      for (const remoteFiltersConfiguration of remoteFiltersConfigurations.value) {
        stagedFilters.value[remoteFiltersConfiguration.filterId] = RemoteFiltersValue.buildDefault(remoteFiltersConfiguration)
      }
    }

    const clearChanges = (): void => {
      setDefaultRemoteFiltersInStagedFilters()

      if (remoteFiltersConfigurations.value.some(filterConfig => filterConfig.relatedFilters.length > 0 || filterConfig.reloadFiltersWhenUpdateValue)) {
        const queryParameters = getQueryParameters(Object.values(stagedFilters.value))
        fetchRemoteFiltersConfiguration(queryParameters).then(() => {
          setDefaultRemoteFiltersInStagedFilters()
          commitChanges()
        })
      } else {
        commitChanges()
      }
    }

    const buildOptionsFromRemoteRelatedValues = (
      filterId: string,
      remoteFilterValue: RemoteFiltersValue,
      filtersConfigurations: RemoteFiltersConfiguration[]
    ): void => {
      const filterConfig = filtersConfigurations.find(config => config.filterId === filterId)
      if (
        filterConfig &&
        filterConfig.filterType === RemoteFiltersFieldType.SELECT_MULTIPLE &&
        filterConfig.options &&
        Array.isArray(remoteFilterValue.value)
      ) {
        const availableOptions = new Set(filterConfig.options.map(option => option.id))
        const filteredValue = remoteFilterValue.value.filter(selectedId => {
          return (
            (typeof selectedId === 'string' || typeof selectedId === 'number') &&
            availableOptions.has(selectedId)
          )
        })
        if (!_isEqual(remoteFilterValue.value, filteredValue)) {
          remoteFilterValue.value = filteredValue as (string | number)[]
        }
      }
    }

    const commitChanges = (): void => {
      const clonedRemoteFilters: Record<string, RemoteFiltersValue> = {}
      const remoteFiltersToSetToURL: Record<string, RemoteFiltersValue> = {}
      for (const [filterId, remoteFilterValue] of Object.entries(stagedFilters.value)) {
        buildOptionsFromRemoteRelatedValues(filterId, remoteFilterValue, remoteFiltersConfigurations.value)
        clonedRemoteFilters[filterId] = remoteFilterValue.clone()
        if (isFilterChanged(remoteFilterValue.configuration)) {
          remoteFiltersToSetToURL[filterId] = remoteFilterValue.clone()
        }
      }
      remoteFilters.value = clonedRemoteFilters
      setURLFilterParameters(Object.values(remoteFiltersToSetToURL))
    }

    const queryParameters = computed<Record<string, string>>(() => {
      let parameters = { ...useSearch.searchQueryParameters.value }
      const availableFilters = moduleRemoteFiltersConfigurations.value.map((rfc) => rfc.filterId)
      for (const [key, value] of Object.entries(remoteFilters.value)) {
        if (value.queryParameters === undefined || !availableFilters.includes(key)) { continue }
        parameters = { ...parameters, ...value.queryParameters }
      }
      return parameters
    })

    const remoteFiltersChips = computed<Record<string, RemoteFiltersChip[]>>(() => {
      const chips: Record<string, RemoteFiltersChip[]> = {}
      moduleRemoteFiltersConfigurations.value.forEach((rfc) => {
        const remoteFilterValue = getRemoteFiltersValue(rfc.filterId)
        if (remoteFilterValue == null || remoteFilterValue.chips.length === 0) { return }
        chips[rfc.label] = remoteFilterValue.chips
      })
      return chips
    })

    return {
      clearChanges,
      commitChanges,
      getRemoteFiltersValue,
      isFilterChanged,
      initialFetch,
      moduleRemoteFilters,
      moduleRemoteFiltersConfigurations,
      queryParameters,
      remoteFilters,
      remoteFiltersChips,
      remoteFiltersConfigurations,
      setRemoteFiltersValue,
      stagedFilters,
      remoteFilterStoreID,
      searchText: useSearch.searchText,
      buildOptionsFromRemoteRelatedValues,
      isFilterActiveInModule,
    }
  })()
}

export default useRemoteFiltersStore
